import { useEffect, useState } from 'react';
import { api, ReviewPayload } from '../api';
import { Icon } from '../Icon';
import { FurtherReading } from '../FurtherReading';

export function Review({ attemptId, onRestart, onSignIn }: {
  attemptId: string; onRestart: () => void; onSignIn: () => void;
}) {
  const [data, setData] = useState<ReviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<ReviewPayload>(`/attempts/${attemptId}/review`).then(setData).catch((e) => setError(e.message));
  }, [attemptId]);

  if (error) return <div className="notice notice--warn" role="alert">{error}</div>;
  if (!data) return <p>Loading your results…</p>;

  const pct = data.denominator ? Math.round((data.rawScore / data.denominator) * 100) : 0;

  return (
    <>
      <h1>Your results</h1>

      <div className="card">
        <p style={{ fontSize: '1.6rem', margin: '0 0 0.25rem' }}>
          <strong>{data.rawScore} of {data.denominator}</strong> correct
          <span className="muted" style={{ fontSize: '1rem' }}> ({pct}%)</span>
        </p>
        <div className="bar-meter" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
        {/* Principle VII: the denominator, the exclusions and the limits of the
            number are stated at the point the number is shown, not in a footer. */}
        <p className="muted" style={{ marginTop: '1rem' }}>{data.disclosure.note}</p>
        {data.exclusions.length > 0 && (
          <div className="notice">
            <strong>{data.exclusions.length} question(s) excluded from scoring:</strong>
            <ul>{data.exclusions.map((e) => <li key={e.questionId}>{e.reason}</li>)}</ul>
          </div>
        )}
        <p className="muted">
          {data.disclosure.bankEdition} · {data.disclosure.contentOwner} · {data.disclosure.independenceStatement}
        </p>
      </div>

      {/* The point of a practice test is what to do next, so this sits directly
          under the score rather than at the bottom of the page. */}
      {(data.focusTopics.session.length > 0 || data.focusTopics.lifetime.length > 0) && (
        <section className="card" aria-labelledby="focus-heading">
          <h2 id="focus-heading" style={{ marginTop: 0 }}>Topics to work on</h2>

          {data.focusTopics.session.length > 0 && (
            <>
              <h3 style={{ marginTop: 0 }}>From this session</h3>
              <ol style={{ paddingLeft: '1.25rem' }}>
                {data.focusTopics.session.map((f) => (
                  <li key={f.topicId} style={{ marginBottom: '0.5rem' }}>
                    <strong>{f.topicName}</strong> — {f.reason}
                  </li>
                ))}
              </ol>
            </>
          )}

          {data.focusTopics.lifetime.length > 0 && (
            <>
              <h3>Across all your attempts</h3>
              <ol style={{ paddingLeft: '1.25rem' }}>
                {data.focusTopics.lifetime.map((f) => (
                  <li key={f.topicId} style={{ marginBottom: '0.5rem' }}>
                    <strong>{f.topicName}</strong> — {f.reason}
                  </li>
                ))}
              </ol>
              <p className="muted">
                A topic appears here once you have answered at least three of its questions and
                are getting fewer than 70% right — one bad session on a topic you usually know
                is not a weakness.
              </p>
            </>
          )}

          {!data.signedIn && (
            <div className="notice">
              These figures cover this session only.{' '}
              <button onClick={onSignIn} style={{ padding: '0.2rem 0.5rem', minHeight: 'auto' }}>
                Create an account
              </button>{' '}
              to keep your history and see which topics need work across every attempt.
            </div>
          )}
        </section>
      )}

      {data.signedIn && data.focusTopics.session.length === 0 && data.focusTopics.lifetime.length === 0 && (
        <div className="notice">
          No topic stands out as weak in this session. Keep going — topics appear here once
          you are getting fewer than 70% right on them.
        </div>
      )}

      <h2>Performance by topic</h2>
      <div className="scroll-x">
        <table>
          <caption className="visually-hidden">Correct answers by topic</caption>
          <thead><tr><th scope="col">Topic</th><th scope="col">Correct</th><th scope="col">Scored</th><th scope="col">Share</th></tr></thead>
          <tbody>
            {data.perTopic.map((t) => (
              <tr key={t.topicId}>
                <th scope="row" style={{ fontWeight: 400 }}>{t.topicName}</th>
                <td>{t.correct}</td><td>{t.scored}</td>
                <td>{t.scored ? Math.round((t.correct / t.scored) * 100) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Question review</h2>
      {data.items.map((item, i) => (
        <article key={item.questionId} className="card">
          <h3 style={{ marginTop: 0 }}>
            Question {i + 1}{' '}
            <span className={`pill ${item.correct ? 'pill--ok' : 'pill--bad'}`}>
              {item.correct ? '✓ Correct' : item.answered ? '✗ Incorrect' : '— Not answered'}
            </span>
            {item.flagged && <span className="pill" style={{ marginLeft: '0.4rem' }}>★ Flagged</span>}
          </h3>
          <p>{item.stem}</p>
          <ul className="review-options">
            {item.options.map((o) => {
              const chosen = item.selectedResponse?.includes(o.key);
              const right = item.correctResponse.includes(o.key);
              return (
                <li key={o.key}
                    className={`review-option${right ? ' review-option--correct' : ''}${
                      chosen && !right ? ' review-option--wrong' : ''}`}>
                  <span className="choice-key">{o.key.toUpperCase()}.</span>{o.text}
                  {/* Wording, not just colour, so the state survives greyscale
                      and a screen reader (FR-069). */}
                  {right && !chosen && <strong className="choice-tag choice-tag--ok"> ✓ Correct answer</strong>}
                  {right && chosen && <strong className="choice-tag choice-tag--ok"> ✓ Correct answer — you chose this</strong>}
                  {chosen && !right && <strong className="choice-tag choice-tag--bad"> ✗ You chose this</strong>}
                </li>
              );
            })}
          </ul>
          {item.rationale && (
            <div className="notice">
              <strong>
                Why {item.correctResponse.join(', ').toUpperCase()} is correct:
              </strong>{' '}
              {item.rationale}
            </div>
          )}
          <FurtherReading links={item.furtherReading ?? []} id={item.questionId} />
          {item.sourceReference && <p className="muted">Source: {item.sourceReference}</p>}
          {item.topicNames.length > 0 && <p className="muted">Topic: {item.topicNames.join(', ')}</p>}
        </article>
      ))}

      <div className="btn-row">
        <button className="btn-primary" onClick={onRestart}><Icon name="rotate-ccw" />Start another session</button>
        <button onClick={() => window.print()}><Icon name="printer" />Print or save this summary</button>
      </div>
    </>
  );
}
